import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from '@mui/material';

import ChangeLogDrawerHeader from './ChangeLogDrawerHeader';

import './_changeLog.scss';

import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import RightArrow from '@/components/Icons/icons/RightArrow';

import { setChangeLogData } from '../../../redux/changelog/changelogActions';
import { GET_CHANGELOG_DATA } from '../../../redux/changelog/changelogType';
import { getChangeLogData } from '../../../services/changelog.service';
import { saralPurple } from '../../../utils/constants';
import ButtonComponent from '../ButtonComponent/ButtonComponent';
import FallbackLoader from '../Loaders/FallbackLoader';
import ThreeDotsLoader from '../Loaders/ThreeDotsLoader';
import ModalComponent from '../ModalComponent/ModalComponent';
import SaralLoaderImage from '../SaralLoaderImage';
import ChangeLogCategoryCard from './ChangeLogCategoryCard';
import ChangeLogContent from './ChangeLogContent';

export const ChangeLogContext = React.createContext({ setRef: () => {}, activeSection: 0 });

const categoriesData = [
  {
    id: 1,
    label: 'What’s New',
    value: 'whatsNew',
    dotColor: 'var(--text-highlight)',
  },
  {
    id: 2,
    label: 'All Updates',
    value: 'allUpdates',
    dotColor: 'var(--text-subdued)',
  },
  {
    id: 3,
    label: 'New Features',
    value: 'newFeature',
    dotColor: '#FF65BC',
  },
  {
    id: 4,
    label: 'Enhancements',
    value: 'enhancements',
    dotColor: '#3D8BF7',
  },
  {
    id: 5,
    label: 'Bug Fixes',
    value: 'bugFix',
    dotColor: '#CA8A04',
  },
];

const ChangeLogDrawer = ({ show, onClose, isWhatsNew }) => {
  const sectionRef = useRef({});
  const dotRef = useRef();
  const lineRef = useRef();

  const [activeSection, setActiveSection] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('allUpdates');
  const { list, next } = useSelector((state) => state.changelog);
  const [fetchingData, setFetchingData] = useState(false);

  const dispatch = useDispatch();

  const scroll = async (e) => {
    if (e.target.scrollTop > 20) {
      document.getElementById('top-header').classList.add('shadow');
    } else {
      document.getElementById('top-header').classList.remove('shadow');
    }
    if (e.target.scrollTop > 20) {
      document.getElementById('top-header').classList.add('shadow');
    } else {
      document.getElementById('top-header').classList.remove('shadow');
    }
    if (sectionRef.current[activeSection]) {
      let newIdx = activeSection;
      let top = sectionRef.current[activeSection].getBoundingClientRect().top;
      if (top < 80 && activeSection + 1 < list.length) {
        newIdx = activeSection + 1;
        setActiveSection(newIdx);
      } else if (activeSection - 1 >= 0) {
        const nextTop = sectionRef.current[activeSection - 1].getBoundingClientRect().top;
        if (nextTop > 80) {
          newIdx = activeSection - 1;
          setActiveSection(newIdx);
        }
      }
      setBlueDot(newIdx);
    }

    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom) {
      if (!isLoading && next) {
        setIsLoading(true);
        const response = await getChangeLogData(list.length, activeCategory);
        if (response?.status) {
          dispatch(setChangeLogData(response.data));
        }
        setIsLoading(false);
      }
    }
  };

  const setBlueDot = (idx) => {
    if (dotRef.current) {
      dotRef.current.style.top = `${sectionRef.current[idx]?.offsetTop + 10}px`;
    }
    if (lineRef.current) {
      lineRef.current.style.height = `${sectionRef.current[idx]?.offsetTop - 50}px`;
    }
  };

  const setRef = (idx, ref) => {
    sectionRef.current[idx] = ref;
  };
  const getCurrentMonth = (logDate) => {
    let date = new Date(logDate);
    const newMonth = dayjs(date).format('MMM YYYY');
    return newMonth;
  };

  const updateChangelogCategory = async (categoryName) => {
    setFetchingData(true);
    setActiveCategory(categoryName);
    setActiveSection(0);
    const payload = categoryName === 'allUpdates' ? '' : categoryName;
    const response = await getChangeLogData(0, payload);
    if (response?.status) {
      dispatch({
        type: GET_CHANGELOG_DATA,
        payload: response.data,
      });
    }
    setFetchingData(false);
  };

  useEffect(() => {
    updateChangelogCategory(isWhatsNew ? 'whatsNew' : 'allUpdates');
  }, [isWhatsNew]);

  const categoryTitle = categoriesData.find(({ value }) => value === activeCategory)?.label;

  return (
    <ModalComponent
      width="1046px"
      show={false}
      onClose={() => {
        updateChangelogCategory('allUpdates');
        onClose();
      }}
      style={{ padding: 0, borderRadius: '16px', overflow: 'hidden' }}
      bodyStyle={{ padding: 0 }}
      outsideClickClose={false}>
      <ChangeLogContext.Provider value={{ setRef, activeSection }}>
        <div className="change-log-wrapper">
          <div id="top-header">
            <ChangeLogDrawerHeader
              title={categoryTitle}
              onClose={() => {
                updateChangelogCategory('allUpdates');
                onClose();
              }}
            />
          </div>
          <div onScroll={scroll} className="change-log-content-wrapper">
            <p className="change-log-current-month">{!fetchingData && getCurrentMonth(list?.[activeSection]?.date)}</p>
            <div className="change-log-drawer-container">
              <div className="change-log-drawer-container-left">
                {!fetchingData && (
                  <>
                    <div ref={dotRef} className="change-log-purple-dot" />
                    <div ref={lineRef} className="change-log-purple-progress" />
                  </>
                )}
              </div>
              {fetchingData ? (
                <div className="changelog-loader">
                  <SaralLoaderImage />
                </div>
              ) : (
                <>
                  <ChangeLogContent onClose={onClose} />
                  {activeCategory === 'whatsNew' && (
                    <div className="view-all-updates-button-container">
                      <ButtonComponent
                        onClick={() => updateChangelogCategory('allUpdates')}
                        variant="text"
                        rightIcon={<RightArrow />}>
                        View all updates
                      </ButtonComponent>
                    </div>
                  )}
                </>
              )}

              {isLoading && !fetchingData && (
                <div style={{ alignSelf: 'center' }}>
                  <ThreeDotsLoader color={saralPurple} />
                </div>
              )}
            </div>
            <div className="change-log-categories">
              <p className="categories-heading">CATEGORIES</p>
              <div className="change-log-options-container">
                {categoriesData.map((category, idx) => (
                  <ChangeLogCategoryCard
                    key={idx}
                    category={category}
                    activeTab={activeCategory}
                    updateChangelogCategory={updateChangelogCategory}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </ChangeLogContext.Provider>
    </ModalComponent>
  );
};

export default ChangeLogDrawer;
